/**
 * Alinq crypto helpers — DID identity, signing, ECDHE handshake, AEAD payloads.
 *
 * All primitives come from audited @noble libraries. No hand-rolled crypto.
 * Private keys live only here, on the owner's device/agent — never on the relay
 * or directory.
 */
import { ed25519, x25519 } from "@noble/curves/ed25519";
import { sha256 } from "@noble/hashes/sha256";
import { hmac } from "@noble/hashes/hmac";
import { hkdf } from "@noble/hashes/hkdf";
import { xchacha20poly1305 } from "@noble/ciphers/chacha";
import { randomBytes } from "@noble/ciphers/webcrypto";
import { base58, base64urlnopad as b64u } from "@scure/base";
import type { DID } from "@alinq/proto";

// --- encoding helpers -------------------------------------------------------

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);
const fromUtf8 = (b: Uint8Array): string => new TextDecoder().decode(b);

export const b64uEncode = (b: Uint8Array): string => b64u.encode(b);
export const b64uDecode = (s: string): Uint8Array => b64u.decode(s);

function concatBytes(...arrs: Uint8Array[]): Uint8Array {
  const total = arrs.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

// --- did:key (Ed25519) ------------------------------------------------------
// did:key uses multibase base58btc (prefix "z") over multicodec ed25519-pub (0xed01)
// followed by the 32-byte Ed25519 public key.

const MULTICODEC_ED25519_PUB = new Uint8Array([0xed, 0x01]);
const DID_KEY_PREFIX = "did:key:z";

export interface Identity {
  did: DID;
  /** Ed25519 secret key (signing). Keep on device only. */
  signPriv: Uint8Array;
  /** Ed25519 public key (signing). */
  signPub: Uint8Array;
  /** X25519 secret key (key agreement). Keep on device only. */
  kaPriv: Uint8Array;
  /** X25519 public key (key agreement). */
  kaPub: Uint8Array;
}

/** A fresh, ephemeral X25519 key-agreement keypair (for per-session forward secrecy). */
export function newKeyAgreementKeypair(): { priv: Uint8Array; pub: Uint8Array } {
  const priv = x25519.utils.randomPrivateKey();
  return { priv, pub: x25519.getPublicKey(priv) };
}

/** Generate a fresh agent identity: an Ed25519 signing key + an X25519 key-agreement key. */
export function createIdentity(): Identity {
  const signPriv = ed25519.utils.randomPrivateKey();
  const signPub = ed25519.getPublicKey(signPriv);
  const kaPriv = x25519.utils.randomPrivateKey();
  const kaPub = x25519.getPublicKey(kaPriv);
  const did = (DID_KEY_PREFIX + base58.encode(concatBytes(MULTICODEC_ED25519_PUB, signPub))) as DID;
  return { did, signPriv, signPub, kaPriv, kaPub };
}

/** Extract the Ed25519 public signing key from a did:key string. */
export function didToSigningKey(did: DID): Uint8Array {
  if (!did.startsWith(DID_KEY_PREFIX)) {
    throw new Error(`not a did:key: ${did}`);
  }
  const decoded = base58.decode(did.slice(DID_KEY_PREFIX.length));
  if (
    decoded.length !== MULTICODEC_ED25519_PUB.length + 32 ||
    decoded[0] !== MULTICODEC_ED25519_PUB[0] ||
    decoded[1] !== MULTICODEC_ED25519_PUB[1]
  ) {
    throw new Error("did:key is not an ed25519-pub key");
  }
  return decoded.slice(MULTICODEC_ED25519_PUB.length);
}

// --- signing ----------------------------------------------------------------

export function sign(msg: Uint8Array, signPriv: Uint8Array): Uint8Array {
  return ed25519.sign(msg, signPriv);
}

export function verify(sig: Uint8Array, msg: Uint8Array, signPub: Uint8Array): boolean {
  return ed25519.verify(sig, msg, signPub);
}

/** Convenience: verify a signature against the public key embedded in a DID. */
export function verifyByDid(sig: Uint8Array, msg: Uint8Array, did: DID): boolean {
  try {
    return verify(sig, msg, didToSigningKey(did));
  } catch {
    return false;
  }
}

// --- hashing / MAC ----------------------------------------------------------

export function sha256Bytes(data: Uint8Array): Uint8Array {
  return sha256(data);
}

/** HMAC-SHA256 over a string message with a string key, returned base64url. */
export function hmacSha256(key: string, message: string): string {
  return b64uEncode(hmac(sha256, utf8(key), utf8(message)));
}

// --- session key from ECDHE (handshake) ------------------------------------

/**
 * Derive a 32-byte AEAD session key from an X25519 ECDHE shared secret.
 * For forward secrecy, callers SHOULD use ephemeral key-agreement keys per
 * session so a leaked session key never decrypts other sessions.
 */
export function deriveSessionKey(myKaPriv: Uint8Array, theirKaPub: Uint8Array): Uint8Array {
  const shared = x25519.getSharedSecret(myKaPriv, theirKaPub);
  return hkdf(sha256, shared, undefined, utf8("alinq/session/v1"), 32);
}

// --- payload encryption (XChaCha20-Poly1305 AEAD) ---------------------------

export interface Sealed {
  /** base64url, 24-byte XChaCha20 nonce. */
  nonce: string;
  /** base64url ciphertext (AEAD includes the Poly1305 tag). */
  ciphertext: string;
}

export function seal(key: Uint8Array, payload: unknown): Sealed {
  const nonce = randomBytes(24);
  const pt = utf8(JSON.stringify(payload));
  const ct = xchacha20poly1305(key, nonce).encrypt(pt);
  return { nonce: b64uEncode(nonce), ciphertext: b64uEncode(ct) };
}

export function open<T = unknown>(key: Uint8Array, nonce: string, ciphertext: string): T {
  const pt = xchacha20poly1305(key, b64uDecode(nonce)).decrypt(b64uDecode(ciphertext));
  return JSON.parse(fromUtf8(pt)) as T;
}

// --- canonical JSON (stable key order, for deterministic signatures) --------

/**
 * Deterministic JSON with recursively sorted object keys, so a signature over a
 * card/relationship verifies regardless of property insertion order. Arrays
 * keep their order.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      out[key] = sortKeys(obj[key]);
    }
    return out;
  }
  return value;
}
